import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import { takeMoveError } from '../auth/runtimeAuth';
import EntraMigrationDialog from './EntraMigrationDialog';

// Pages where the popup never appears: the clinical apps (a doctor may be in the
// middle of a resuscitation) and the screens of sign-in and onboarding.
const isClinicalPath = (pathname) => pathname.startsWith('/main/')
  || pathname.startsWith('/checklists/instance/');

// The migration popup for a legacy session (docs/EntraFullMigrationPlan.md §3.2,
// D18). When it is due comes from the server (/api/users/me →
// entraMigrationPrompt), which counts the "later"s on the account: 3 of them,
// 3 days apart, then a blocking screen. Also shows why a move just failed.
const EntraMigrationPrompt = ({ fullPage }) => {
  const { user, authMethod, isInitialized } = useContext(UserContext);
  const { pathname } = useLocation();
  const [moveError] = useState(() => takeMoveError() || '');
  const [state, setState] = useState(null);
  const [closed, setClosed] = useState(false);
  const shownRecorded = useRef(false);

  useEffect(() => {
    setState(user?.entraMigrationPrompt || null);
  }, [user]);

  const eligible = isInitialized && authMethod === 'legacy' && Boolean(user?.canMoveToEntra)
    && !fullPage && !isClinicalPath(pathname);
  const due = Boolean(state?.show);
  const blocking = due && Boolean(state?.blocking);
  // The blocking screen cannot be closed; anything else closes until the next load.
  const open = eligible && (blocking || (!closed && (due || Boolean(moveError))));

  // After "later" the popup comes back by itself when the wait ends (nextAt from
  // the server), without a reload: a tab may stay open for days.
  useEffect(() => {
    if (!state || state.show || !state.nextAt) return undefined;
    const wait = Math.max(0, new Date(state.nextAt).getTime() - Date.now());
    // setTimeout overflows past ~24.8 days; the wait is never that long.
    const timer = setTimeout(() => {
      setState(current => (current ? { ...current, show: true, nextAt: null } : current));
      setClosed(false);
    }, Math.min(wait, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [state]);

  useEffect(() => {
    if (!open || !due || shownRecorded.current) return;
    shownRecorded.current = true;
    API.post('/api/users/me/entra-migration/prompt', { action: 'shown' }, { handlingErrorLocally: true })
      .catch(error => console.error('Recording the migration popup failed:', error));
  }, [open, due]);

  const later = async () => {
    setClosed(true);
    // A popup opened only to show a failed move is not a "later".
    if (!due) return;
    try {
      const response = await API.post(
        '/api/users/me/entra-migration/prompt',
        { action: 'dismissed' },
        { handlingErrorLocally: true },
      );
      setState(response.data.entraMigrationPrompt);
    } catch (error) {
      console.error('Recording "later" failed:', error);
    }
  };

  if (!eligible) return null;
  return (
    <EntraMigrationDialog
      open={open}
      blocking={blocking}
      onLater={later}
      initialError={moveError}
    />
  );
};

EntraMigrationPrompt.propTypes = {
  fullPage: PropTypes.bool.isRequired,
};

export default EntraMigrationPrompt;
